"use client"

import type { AvailableFilterOptions } from "@/features/companiesSearch/types"
import type { FormEventHandler, FunctionComponent, KeyboardEvent } from "react"

import { useSearchParams } from "next/navigation"
import { useRef } from "react"

import { Checkbox } from "@jobteaser/spark/components/Checkbox"
import { Search } from "@jobteaser/spark/components/Icons/Search"
import { MultiSelect } from "@jobteaser/spark/components/MultiSelect"
import { TextInput } from "@jobteaser/spark/components/TextInput"

import { useCompaniesFilters } from "@/features/companiesSearch/hooks/useCompaniesFilters"
import { useLayoutContext } from "@/modules/landingSearch/components/Layout/LayoutContextProvider"

import styles from "@/modules/landingSearch/components/Filters/Filters.module.css"

type FiltersProps = {
  businessTypesLabel: string
  clearIconAriaLabel: string
  domainsLabel: string
  exclusiveLabel: string
  isExclusiveFilterEnabled: boolean
  options: AvailableFilterOptions
  queryPlaceholder: string
  searchLabel: string
}

export const Filters: FunctionComponent<FiltersProps> = ({
  businessTypesLabel,
  clearIconAriaLabel,
  domainsLabel,
  exclusiveLabel,
  isExclusiveFilterEnabled,
  options,
  queryPlaceholder,
  searchLabel,
}) => {
  const { navigateWithUpdatedQueryParams } = useCompaniesFilters()
  const searchParams = useSearchParams()
  const formRef = useRef<HTMLFormElement>(null)
  const { isSearchLoading } = useLayoutContext()

  function submitForm(): void {
    if (!formRef.current) {
      return
    }

    const formData = new FormData(formRef.current)
    const sanitizedExclusive = (formData.get("exclusive") as string) ?? "false"
    const sanitizedQuery = ((formData.get("query") as string) || "").trim() || null

    navigateWithUpdatedQueryParams({
      exclusive: sanitizedExclusive,
      query: sanitizedQuery,
    })
  }

  const onSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()
    submitForm()
  }

  const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      submitForm()
    }
  }

  const onFieldClearing = (): void => {
    setTimeout(() => {
      submitForm()
    })
  }

  const onFieldUpdate: FormEventHandler<HTMLInputElement> = (event): void => {
    if (event.currentTarget.type === "checkbox") {
      return submitForm()
    }

    const newValue = event.currentTarget.value.trim()
    const oldValue = (searchParams.get(event.currentTarget.name) || "").trim()
    const isFieldPristine = !newValue && !oldValue
    const isFieldUnchanged = newValue === oldValue

    if (isFieldPristine || isFieldUnchanged) {
      return undefined
    }

    return submitForm()
  }

  const onMultiSelectChange =
    (fieldName: string) =>
    (selectedValues: string[]): void => {
      navigateWithUpdatedQueryParams({
        [fieldName]: selectedValues.length ? selectedValues : null,
      })
    }

  return (
    <form className={styles.main} onSubmit={onSubmit} ref={formRef}>
      <div className={styles.mainBar}>
        <TextInput
          className={styles.primaryFilter}
          clearIconAriaLabel={clearIconAriaLabel}
          hasClearIcon
          id="query"
          inputClassName={styles.primaryFilterFieldInput}
          leftIcon={Search}
          name="query"
          onBlur={onFieldUpdate}
          onClear={onFieldClearing}
          onKeyDown={handleInputKeydown}
          placeholder={queryPlaceholder}
          readOnly={isSearchLoading}
          value={searchParams.get("query") || ""}
          aria-label={searchLabel}
        />
        <MultiSelect
          className={styles.primaryFilter}
          clearIconLabel={clearIconAriaLabel}
          defaultValue={searchParams.getAll("businessTypes")}
          id="businessTypes"
          aria-label={businessTypesLabel}
          multipleSelectedOptionsTextFormatter={String}
          name="businessTypes"
          onSelect={onMultiSelectChange("businessTypes")}
          options={options.businessTypes}
          placeholder={businessTypesLabel}
          readOnly={isSearchLoading}
        />
        <MultiSelect
          className={styles.primaryFilter}
          clearIconLabel={clearIconAriaLabel}
          defaultValue={searchParams.getAll("domains")}
          id="domains"
          aria-label={domainsLabel}
          multipleSelectedOptionsTextFormatter={String}
          name="domains"
          onSelect={onMultiSelectChange("domains")}
          options={options.domains}
          placeholder={domainsLabel}
          readOnly={isSearchLoading}
        />
      </div>
      {isExclusiveFilterEnabled && (
        <div className={styles.secondaryBar}>
          <Checkbox
            checked={searchParams.get("exclusive") === "true"}
            className={styles.secondaryFilter}
            disabled={isSearchLoading}
            name="exclusive"
            onChange={onFieldUpdate}
            value="true"
          >
            {exclusiveLabel}
          </Checkbox>
        </div>
      )}
    </form>
  )
}
